import LocalizedMessage from '../common/LocalizedMessage';
/* eslint jsx-a11y/label-has-associated-control: ["error", {"assert": "either"}] */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link, Route, Switch } from "react-router-dom";
import service from "../../services/SupportReportService";
import { useSupport } from "./SupportReportsProvider";
import { STATUS_LABELS, TYPE_LABELS, requestId } from "./supportLabels";
import { useTranslation } from 'react-i18next';

const date = (value, language) => new Date(value).toLocaleString(language === 'en' ? 'en-GB' : 'pl-PL', { hour12: false });
function SupportList({ platform }) {
  const { t: tr , i18n } = useTranslation();
  const { capabilities, capabilityError, refresh, base, generation } =
    useSupport();
  const [selectedScope, setScope] = useState(null);
  const scope = capabilities && capabilities.canReadSchool ? selectedScope || "school" : "mine";
  const [status, setStatus] = useState("ACTIVE");
  const [schoolId, setSchool] = useState("");
  const [page, setPage] = useState({ items: [], nextCursor: null });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const revision = useRef(0);
  const load = async (cursor) => {
    const current = ++revision.current;
    setBusy(true);
    setError("");
    try {
      const result = await service.listReports({
        platform,
        scope,
        status,
        schoolId: platform ? schoolId : undefined,
        cursor,
      });
      if (current === revision.current) {
        setPage((old) => ({
          ...result,
          items: Array.from(
            new Map(
              [...(cursor ? old.items : []), ...result.items].map((item) => [
                item.reportId,
                item,
              ]),
            ).values(),
          ),
        }));
      }
    } catch (failure) {
      if (current === revision.current) setError(failure.message);
    } finally {
      if (current === revision.current) setBusy(false);
    }
  };
  useEffect(() => {
    setPage({ items: [], nextCursor: null });
    if (capabilities) load();
    return () => {
      revision.current += 1;
    };
  }, [scope, status, schoolId, platform, generation, Boolean(capabilities)]);
  if (platform && (!capabilities || !capabilities.canHandlePlatform)) return <p role="alert">{tr("manage:integratedText32")}</p>;
  return (
    <section className="support-page">
      <h1>{platform ? tr("manage:integratedText33") : tr("manage:integratedText34")}</h1>
      <div className="support-toolbar">
        {capabilities && capabilities.canHandlePlatform && (
          <Link
            className="btn manage-teal-button"
            to={platform ? base : `${base}/platform`}
          >
            {platform ? tr("manage:integratedText34") : tr("manage:integratedText35")}
          </Link>
        )}
        {!platform && capabilities && capabilities.canReadSchool && (
          <label>{tr("manage:integratedText36")}<select
              className="form-control"
              value={scope}
              onChange={(event) => setScope(event.target.value)}
            >
              <option value="mine">{tr("manage:integratedText37")}</option>
              <option value="school">{tr("manage:integratedText38")}</option>
            </select>
          </label>
        )}
        {platform && (
          <label>{tr("manage:integratedText39")}<input
              className="form-control"
              value={schoolId}
              onChange={(event) => setSchool(event.target.value)}
              placeholder={tr("manage:integratedText40")}
            />
          </label>
        )}
        <label>{tr("manage:status_146")}<select
            className="form-control"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="ACTIVE">{tr("manage:integratedText41")}</option>
            <option value="ARCHIVED">{tr("manage:integratedText6")}</option>
          </select>
        </label>
        <button
          type="button"
          className="btn manage-teal-button"
          disabled={busy}
          onClick={() => {
            refresh();
            load();
          }}
        >{tr("public:refresh_169")}</button>
      </div>
      {capabilityError && (
        <p role="alert">{tr("manage:integratedText42")} <LocalizedMessage isError value={capabilityError} />
        </p>
      )}
      {capabilities && !capabilities.enabled && !platform && (
        <p className="alert alert-info">{tr("manage:integratedText43")}</p>
      )}
      {error && (
        <p role="alert" className="alert alert-danger">
          <LocalizedMessage isError value={error} />
        </p>
      )}
      {!busy && !error && page.items.length === 0 && (
        <p>{tr("manage:integratedText44")}</p>
      )}
      <div className="support-list">
        {page.items.map((report) => (
          <Link
            className="support-report-card"
            key={report.reportId}
            to={
              platform
                ? `${base}/platform/${report.schoolId}/${report.reportId}`
                : `${base}/${report.reportId}?school=${encodeURIComponent(report.schoolId)}`
            }
          >
            <div>
              <span className="support-status">
                {tr(`manage:support.status.${report.status}`)}
              </span>{" "}
              <span>{tr(`manage:support.type.${report.type}`)}</span>
            </div>
            <h2>{report.description.slice(0, 150)}</h2>
            <p>
              {report.authorName}
              {platform ? tr("manage:reportSchool", { school: report.schoolId }) : ""}
            </p>
            <small>
              {report.reportNumber ? tr("manage:reportNumber", { number: report.reportNumber }) : tr("manage:integratedText45")} · {date(report.updatedAt, i18n.language)}
            </small>
          </Link>
        ))}
      </div>
      {busy && <p role="status">{tr("manage:integratedText46")}</p>}
      {page.nextCursor && (
        <button
          type="button"
          className="btn manage-teal-button"
          disabled={busy}
          onClick={() => load(page.nextCursor)}
        >{tr("manage:integratedText47")}</button>
      )}
    </section>
  );
}
SupportList.propTypes = { platform: PropTypes.bool };
SupportList.defaultProps = { platform: false };

export function SupportDetail({ target, requiredSchool }) {
  const { t: tr , i18n } = useTranslation();
  const { user, base } = useSupport();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");
  const [status, setStatus] = useState("");
  const [explanation, setExplanation] = useState("");
  const [uncertain, setUncertain] = useState(false);
  const command = useRef(null);
  const alive = useRef(true);
  const [image, setImage] = useState("");
  const [imageError, setImageError] = useState("");
  const [imageBusy, setImageBusy] = useState(false);
  const deniedSchool =
    !target.platform && requiredSchool && requiredSchool !== user.schoolId;
  const loadImage = async () => {
    setImageBusy(true);
    setImageError("");
    try {
      const result = await service.getAttachment(target);
      if (alive.current) setImage(result.url);
    } catch (failure) {
      if (alive.current) setImageError(failure.message);
    } finally {
      if (alive.current) setImageBusy(false);
    }
  };
  const hasAttachment = Boolean(data && data.report.hasAttachment);
  useEffect(() => {
    if (hasAttachment && !deniedSchool) loadImage();
  }, [hasAttachment]);
  const load = async () => {
    try {
      const result = await service.getReport(target);
      if (alive.current) {
        setData(result);
        setError("");
      }
    } catch (failure) {
      if (alive.current) setError(failure.message);
    }
  };
  useEffect(() => {
    if (!deniedSchool) load();
    return () => {
      alive.current = false;
    };
  }, []);
  const send = async (kind) => {
    if (busy) return;
    setBusy(true);
    setError("");
    if (!command.current) {
      command.current = {
        kind,
        body:
          kind === "reply"
            ? { requestId: requestId(), text: reply }
            : {
              requestId: requestId(),
              status,
              expectedVersion: data.report.version,
              explanation,
            },
      };
    }
    try {
      const pending = command.current;
      await service[pending.kind === "reply" ? "addMessage" : "changeStatus"](
        target,
        pending.body,
      );
      if (!alive.current) return;
      command.current = null;
      setUncertain(false);
      if (pending.kind === "reply") setReply("");
      else {
        setExplanation("");
        setStatus("");
      }
      await load();
    } catch (failure) {
      if (!alive.current) return;
      const unknown = !failure.status || failure.status >= 500;
      setUncertain(unknown);
      if (!unknown) command.current = null;
      if (failure.code === "VERSION_CONFLICT") await load();
      setError(
        unknown
          ? tr("manage:integratedText48")
          : failure.message,
      );
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  const more = async () => {
    setBusy(true);
    try {
      const page = await service.listEvents(target, data.events.nextCursor);
      if (alive.current) {
        setData((old) => ({
          ...old,
          events: { ...page, items: [...old.events.items, ...page.items] },
        }));
      }
    } catch (failure) {
      if (alive.current) setError(failure.message);
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  if (deniedSchool) {
    return (
      <div className="alert alert-info">{tr("manage:integratedText49")}</div>
    );
  }
  return (
    <section className="support-page">
      <Link
        className="btn manage-teal-button mb-3"
        to={target.platform ? `${base}/platform` : base}
      >{tr("manage:integratedText50")}</Link>
      {error && (
        <p className="alert alert-warning" role="alert">
          <LocalizedMessage isError value={error} />
        </p>
      )}
      {!data && !error && <p role="status">{tr("manage:integratedText51")}</p>}
      {!data && error && (
        <button type="button" className="btn manage-teal-button" onClick={load}>{tr("manage:integratedText52")}</button>
      )}
      {data && (
        <>
          <h1>
            {tr(`manage:support.type.${data.report.type}`)}{" "}
            <span className="support-status">
              {tr(`manage:support.status.${data.report.status}`)}
            </span>
          </h1>
          <p className="small">
            {data.report.reportNumber ? tr("manage:reportNumber", { number: data.report.reportNumber }) : tr("manage:integratedText45")} · {data.report.authorName} ·{" "}
            {date(data.report.createdAt, i18n.language)}
          </p>
          <p className="support-text">{data.report.description}</p>
          {data.report.source && (
            <p className="small">{tr("manage:integratedText53")}{" "}
              {data.report.source.viewLabel || data.report.source.pathname}
            </p>
          )}
          {imageBusy && <p role="status">{tr("manage:integratedText54")}</p>}
          {imageError && <p role="alert">{tr("manage:integratedText55")} <LocalizedMessage isError value={imageError} /></p>}
          {imageError && (
            <button
              type="button"
              className="btn manage-teal-button"
              onClick={loadImage}
            >{tr("manage:integratedText56")}</button>
          )}
          {image && (
            <img
              className="support-attachment"
              src={image}
              alt={tr("manage:integratedText57")}
              onError={() => {
                setImage("");
                setImageError(tr("manage:integratedText58"));
              }}
            />
          )}
          <h2 className="h4 mt-4">{tr("manage:integratedText59")}</h2>
          <ol className="support-timeline">
            {data.events.items.map((event) => (
              <li key={event.eventId}>
                <strong>{event.actorName}</strong>{" "}
                <small>{date(event.createdAt, i18n.language)}</small>
                {event.type === "SUPPORT_REPORT_CREATED" && (
                  <p>{tr("manage:integratedText60")}</p>
                )}
                {event.toStatus && (
                  <p>{tr("manage:integratedText61")} {tr(`manage:support.status.${event.fromStatus}`)} →{" "}
                    {tr(`manage:support.status.${event.toStatus}`)}
                  </p>
                )}
                {event.text && <p className="support-text">{event.text}</p>}
              </li>
            ))}
          </ol>
          {data.events.nextCursor && (
            <button
              type="button"
              className="btn manage-teal-button"
              disabled={busy}
              onClick={more}
            >{tr("manage:integratedText62")}</button>
          )}
          {uncertain && (
            <button
              type="button"
              className="btn manage-red-button"
              disabled={busy}
              onClick={() => send(command.current.kind)}
            >{tr("manage:integratedText9")}</button>
          )}
          {data.report.canReply && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                send("reply");
              }}
              className="mt-4"
            >
              <label htmlFor="support-reply">{tr("manage:integratedText63")}</label>
              <textarea
                id="support-reply"
                className="form-control"
                rows="4"
                required
                readOnly={busy || uncertain}
                value={reply}
                onChange={(event) => setReply(event.target.value)}
              />
              <p className="support-count">{[...reply].length}{tr("manage:integratedText17")}</p>
              <button
                type="submit"
                className="btn manage-red-button"
                disabled={
                  busy ||
                  uncertain ||
                  !reply.trim() ||
                  [...reply.trim()].length > 5000
                }
              >{tr("manage:integratedText64")}</button>
            </form>
          )}
          {data.report.allowedTransitions.length > 0 && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                send("status");
              }}
              className="mt-4"
            >
              <label htmlFor="support-status">{tr("manage:integratedText65")}</label>
              <select
                id="support-status"
                className="form-control"
                required
                disabled={busy || uncertain}
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="">{tr("manage:integratedText66")}</option>
                {data.report.allowedTransitions.map((key) => (
                  <option key={key} value={key}>
                    {tr(`manage:support.status.${key}`)}
                  </option>
                ))}
              </select>
              {status === "REJECTED" && (
                <label className="d-block mt-2" htmlFor="support-explanation">{tr("manage:integratedText67")}<textarea
                    id="support-explanation"
                    className="form-control"
                    required
                    value={explanation}
                    readOnly={busy || uncertain}
                    onChange={(event) => setExplanation(event.target.value)}
                  />
                </label>
              )}
              <button
                type="submit"
                className="btn manage-red-button mt-2"
                disabled={
                  busy ||
                  uncertain ||
                  !status ||
                  (status === "REJECTED" &&
                    (!explanation.trim() || [...explanation].length > 5000))
                }
              >{tr("manage:integratedText68")}</button>
            </form>
          )}
        </>
      )}
    </section>
  );
}
SupportDetail.propTypes = {
  target: PropTypes.object.isRequired,
  requiredSchool: PropTypes.string,
};
SupportDetail.defaultProps = { requiredSchool: null };
export default function SupportRoutes() {
  const { base } = useSupport();
  return (
    <Switch>
      <Route
        exact
        path={`${base}/platform/:schoolId/:reportId`}
        render={({ match }) => (
          <SupportDetail
            key={JSON.stringify(match.params)}
            target={{ ...match.params, platform: true }}
          />
        )}
      />
      <Route
        exact
        path={`${base}/platform`}
        render={() => <SupportList platform />}
      />
      <Route
        exact
        path={`${base}/:reportId`}
        render={({ match, location }) => (
          <SupportDetail
            key={`${match.params.reportId}${location.search}`}
            target={match.params}
            requiredSchool={new URLSearchParams(location.search).get("school")}
          />
        )}
      />
      <Route render={() => <SupportList />} />
    </Switch>
  );
}
